import { useFetcher } from '@remix-run/react'
import type { SerializeFrom } from '@remix-run/server-runtime'
import { LinkIcon, Trash2Icon } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { makeShopOwnPath } from '~/helpers/routes'
import { useShop } from '~/hooks/data/use-shop'
import { useDebounce } from '~/hooks/use-debounce'
import type { Collection } from '~/models/collection.server'
import type { ProductFull } from '~/models/product.server'
import { Button, SegmentedControl } from '~/ui'
import { Popover, usePopoverContext } from '~/ui/popover'

type SliderLinkProps = {
  sliderItemId: string
  currentLink?: string | null
}

export const ShopSliderLink = ({
  sliderItemId,
  currentLink,
}: SliderLinkProps) => {
  const { t } = useTranslation()
  const shop = useShop()
  const fetcher = useFetcher()
  const { linkName, isLoading: isLoadingLink } = useLinkedItemName(currentLink)

  const onLinkChange = (link: string) => {
    fetcher.submit(
      { link },
      {
        method: 'PATCH',
        action: makeShopOwnPath(shop, `/attachments/${sliderItemId}`),
      }
    )
  }

  const handleRemoveLink = () => {
    fetcher.submit(
      { link: '' },
      {
        method: 'PATCH',
        action: makeShopOwnPath(shop, `/attachments/${sliderItemId}`),
      }
    )
  }

  if (currentLink) {
    return (
      <Button
        size="xs"
        variant="ghost"
        className="mt-2 text-sm text-white"
        onClick={handleRemoveLink}
        disabled={isLoadingLink}
      >
        <LinkIcon />
        <span className="max-w-[10ch] truncate @lg:max-w-[15ch]">
          {!linkName && isLoadingLink ? 'Loading...' : linkName || 'Linked'}
        </span>
        <Trash2Icon />
      </Button>
    )
  }

  return (
    <Popover placement="top" modal>
      <Popover.Trigger asChild>
        <Button size="xs" variant="ghost" className="mt-2 text-sm text-white">
          <LinkIcon />
          <span className="max-w-[10ch] truncate @lg:max-w-[15ch]">
            {t('components.slider_item_link.title')}
          </span>
        </Button>
      </Popover.Trigger>
      <Popover.Content data-theme="protocol-dark" className="p-0">
        <ShopSliderLinkContent onLinkChange={onLinkChange} />
      </Popover.Content>
    </Popover>
  )
}

type ShopSliderLinkContentProps = {
  onLinkChange: (link: string) => void
}

const ShopSliderLinkContent = ({
  onLinkChange,
}: ShopSliderLinkContentProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const [tab, setTab] = useState<'products' | 'collections'>('products')

  const debouncedSearch = useDebounce(search, 300)

  const { items, isLoading } = useSliderLinkSearch({
    type: tab,
    search: debouncedSearch,
  })

  return (
    <div>
      <Popover.SearchInput
        autoFocus
        placeholder={t('components.slider_item_link.search_placeholder', {
          defaultValue: 'Search your store',
        })}
        value={search}
        onChange={(e) => setSearch(e.currentTarget.value)}
        isLoading={isLoading}
      />
      <div className="p-1">
        <SegmentedControl
          value={tab}
          onValueChange={(value) => {
            setTab(value as 'products' | 'collections')
            setSearch('') // Reset search when switching tabs
          }}
        >
          <SegmentedControl.Item className="grow" value="products">
            {t('noun.products', { defaultValue: 'Products' })}
          </SegmentedControl.Item>
          <SegmentedControl.Item className="grow" value="collections">
            {t('noun.collections', { defaultValue: 'Collections' })}
          </SegmentedControl.Item>
        </SegmentedControl>
      </div>
      <div
        className="h-[200px] overflow-y-auto px-1"
        inert={isLoading || undefined}
      >
        <div className="space-y-1">
          {tab === 'products'
            ? (items as SerializeFrom<ProductFull>[]).map((product) => (
                <PopoverItemButton
                  key={product.id}
                  onClick={() => {
                    onLinkChange(`/products/${product.id}/${product.slug}`)
                  }}
                >
                  {product.name}
                </PopoverItemButton>
              ))
            : (items as SerializeFrom<Collection>[]).map((collection) => (
                <PopoverItemButton
                  key={collection.id}
                  onClick={() => {
                    onLinkChange(
                      `/collections/${collection.id}/${collection.slug}`
                    )
                  }}
                >
                  {collection.name}
                </PopoverItemButton>
              ))}
        </div>
      </div>
    </div>
  )
}

// Custom hook for fetching products/collections with search
const useSliderLinkSearch = ({
  type,
  search,
}: {
  type: 'products' | 'collections'
  search: string
}) => {
  const shop = useShop()
  const { load, data, state } = useFetcher<{
    products?: SerializeFrom<ProductFull>[]
    collections?: SerializeFrom<Collection>[]
  }>()

  // Fetch when type or search changes
  useEffect(() => {
    const params = new URLSearchParams({
      limit: '5',
      page: '1',
    })

    if (search) {
      params.set('search', search)
    }

    if (type === 'products') {
      // Fetch only published products
      params.set('status', 'live')
      const path = makeShopOwnPath(
        shop,
        `/products/search?${params.toString()}`
      )
      load(path)
    } else {
      // Fetch only published collections
      params.set('status', 'live')
      const path = makeShopOwnPath(
        shop,
        `/collections/search?${params.toString()}`
      )
      load(path)
    }
  }, [type, search, shop, load])

  const items = useMemo(() => {
    if (type === 'products') {
      return data?.products ?? []
    }
    return data?.collections ?? []
  }, [data, type])

  const isLoading = state === 'loading'

  return { items, isLoading }
}

// Hook to fetch the linked item's name
const useLinkedItemName = (link: string | null | undefined) => {
  const shop = useShop()
  const { load, data, state } = useFetcher<{ name: string }>()

  useEffect(() => {
    if (!link) return

    // Extract type and ID from link: /products/{id}/{slug} or /collections/{id}/{slug}
    const parts = link.split('/')
    if (parts.length < 3) return

    const type = parts[1] // 'products' or 'collections'
    const id = parts[2]

    if (!type || !id) return

    // Fetch just the name from dedicated endpoint
    const path = makeShopOwnPath(shop, `/${type}/${id}/name`)
    load(path)
  }, [link, shop, load])

  const linkName = data?.name ?? null
  const isLoading = state === 'loading'

  return { linkName, isLoading }
}

const PopoverItemButton = ({
  onClick,
  children,
}: {
  onClick: () => void
  children: React.ReactNode
}) => {
  const { setOpen } = usePopoverContext()

  return (
    <button
      type="button"
      onClick={() => {
        onClick()
        setOpen(false)
      }}
      className="hover:bg-foreground/5 h-9 w-full cursor-pointer rounded-md px-3 text-left font-medium"
    >
      {children}
    </button>
  )
}
